import React, { useState, useEffect } from 'react';
import {
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  MenuItem,
  IconButton,
  Box,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditItemView from '../common/components/EditItemView';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getWithExpiry } from '../common/util/localstorage';

const useStyles = makeStyles((theme) => ({
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  resourceRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
}));

const MaintenancePage = () => {
  const classes = useStyles();
  const { id } = useParams();
  const [item, setItem] = useState({ resources: [] });
  const products = useSelector((state) => state.products.items);
  const [searchKeyword, setSearchKeyword] = useState('');
  const _isAdministrator = useSelector((state) => state.session.user?.role);

  // All possible status options
  const allStatusOptions = [
    'en attente',
    'en cours de reparation',
    'reparer',
    'retour',
    'livré',
    'non réparable',
    'aucun defaut TEST OK',
    'annuler'
  ];

  // Filtered status options based on user role
  const statusOptions =
    _isAdministrator === 'superadmin' || _isAdministrator === 'admin'
      ? allStatusOptions
      : allStatusOptions.filter((s) => s !== 'livré' && s !== 'retour'&& s !== 'annuler');

  useEffect(() => {
    if (!id) return;
    const fetchMaintenance = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_URL_NAME}/maintenances/${id}`, {
          headers: { Authorization: `Bearer ${getWithExpiry('TOKEN')}` },
        });
        if (res.ok) {
          const data = await res.json();
          setItem({ resources: [], ...data });
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchMaintenance();
  }, [id]);

  const validate = () => item && item.produit && item.statut && item.datetime_debut;

  const getDuration = (start, end) => {
    if (!start) return '0 j';
    const s = new Date(start);
    const e = end ? new Date(end) : new Date();
    let delta = Math.floor((e - s) / (1000 * 60 * 60 * 24));
    const years = Math.floor(delta / 365);
    delta -= years * 365;
    const months = Math.floor(delta / 30);
    delta -= months * 30;
    const days = delta;
    let str = '';
    if (years) str += `${years} an${years > 1 ? 's' : ''} `;
    if (months) str += `${months} mois `;
    if (days) str += `${days} j`;
    return str || '0 j';
  };

  const addResource = () =>
    setItem({ ...item, resources: [...item.resources, { nom: '', quantite: 0, unite: '' }] });

  const removeResource = (index) => {
    const newResources = [...item.resources];
    newResources.splice(index, 1);
    setItem({ ...item, resources: newResources });
  };

  const updateResource = (index, key, value) => {
    const newResources = [...item.resources];
    if (key === 'quantite') value = Number(value);
    newResources[index][key] = value;
    setItem({ ...item, resources: newResources });
  };

  return (
    <EditItemView endpoint="maintenances" item={item} setItem={setItem} validate={validate}>
      {item && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Informations</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            {/* Sélection du produit */}
            <TextField
              select
              value={item.produit?._id || ''}
              onChange={(e) =>
                setItem({ ...item, produit: products.find((p) => p._id === e.target.value) })
              }
              label="Produit"
              required
              SelectProps={{
                renderValue: (selected) => {
                  const prod = products.find((p) => p._id === selected);
                  return prod ? `${prod.reference} - ${prod.designation}` : '';
                },
              }}
            >
              {products
                .filter((p) =>
                  p.reference.toLowerCase().includes(searchKeyword.toLowerCase())
                )
                .map((prod) => (
                  <MenuItem key={prod._id} value={prod._id}>
                    {prod.reference} - {prod.designation}
                  </MenuItem>
                ))}
            </TextField>

            {/* Statut (filtered based on role) */}
            <TextField
              select
              value={item.statut || ''}
              onChange={(e) => setItem({ ...item, statut: e.target.value })}
              label="Statut"
              required
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>

            {/* Dates */}
            <TextField
              type="datetime-local"
              value={
                item.datetime_debut
                  ? new Date(item.datetime_debut).toISOString().slice(0, 16)
                  : ''
              }
              onChange={(e) => setItem({ ...item, datetime_debut: e.target.value })}
              label="Date début"
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              type="datetime-local"
              value={
                item.datetime_fin
                  ? new Date(item.datetime_fin).toISOString().slice(0, 16)
                  : ''
              }
              onChange={(e) => setItem({ ...item, datetime_fin: e.target.value })}
              label="Date fin"
              InputLabelProps={{ shrink: true }}
            />

            {item.datetime_debut && (
              <TextField
                value={getDuration(item.datetime_debut, item.datetime_fin)}
                label="Durée"
                InputProps={{ readOnly: true }}
              />
            )}

            {/* Remarques */}
            <TextField
              value={item.remarque || ''}
              onChange={(e) => setItem({ ...item, remarque: e.target.value })}
              label="Remarque"
              multiline
              minRows={2}
            />

            <TextField
              value={item.commentaire || ''}
              onChange={(e) => setItem({ ...item, commentaire: e.target.value })}
              label="Commentaire"
              multiline
              minRows={2}
            />

            {/* Resources */}
            <Box>
              <Typography variant="subtitle2">Resources</Typography>
              {item.resources.map((res, index) => (
                <Box key={index} className={classes.resourceRow}>
                  <TextField
                    label="Nom"
                    value={res.nom || ''}
                    onChange={(e) => updateResource(index, 'nom', e.target.value)}
                  />
                  <TextField
                    type="number"
                    label="Quantité"
                    value={res.quantite || 0}
                    onChange={(e) => updateResource(index, 'quantite', e.target.value)}
                  />
                  <TextField
                    label="Unité"
                    value={res.unite || ''}
                    onChange={(e) => updateResource(index, 'unite', e.target.value)}
                  />
                  <IconButton onClick={() => removeResource(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <IconButton onClick={addResource} size="small" color="primary">
                <AddIcon />
              </IconButton>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </EditItemView>
  );
};

export default MaintenancePage;
